import { ShieldPlusIcon } from 'lucide-react'

import { CarrierLogo } from 'components/members/components/CarrierLogo'
import { Badge } from 'slab'

import { AncillaryFeaturesCard } from './AncillaryFeaturesCard'
import { ancillaryDescriptionToLineItem, getDentalCoverage, getVisionLineItems } from '../../smartBudgetUtils'
import {
  type DentalPlan,
  type NonMedicalPlanFeature,
  type VisionPlan,
} from '../../types'

const DentalFeatures = ({ features }: { features: NonMedicalPlanFeature[] }) => {
  const deductiblesAndLimits = features.find((feature) => feature.header === 'Deductibles and limits')
  const coverage = features.filter((feature) => feature.header !== 'Deductibles and limits')

  return (
    <div className='flex flex-col gap-4'>
      {deductiblesAndLimits && (
        <AncillaryFeaturesCard
          title='Deductibles and limits'
          lineItems={
            deductiblesAndLimits.descriptions.map((description) => {
              return ancillaryDescriptionToLineItem(description)
            })
          }
        />
      )}
      {coverage && coverage.length > 0 && (
        <AncillaryFeaturesCard
          title='Coverage'
          lineItems={getDentalCoverage(coverage)}
        />
      )}
    </div>
  )
}

const VisionFeatures = ({ features }: { features: NonMedicalPlanFeature[] }) => {
  return (
    features.map((feature) => {
      return (
        <AncillaryFeaturesCard
          key={feature.header}
          title={feature.header}
          lineItems={getVisionLineItems(feature.descriptions)}
        />
      )
    })
  )
}

export const AncillaryDetails = ({
  ancillaryPlan,
}: {
  ancillaryPlan: DentalPlan | VisionPlan;
}) => {
  const isVisionPlan = 'description' in ancillaryPlan
  const isDentalPlan = 'waiting_period' in ancillaryPlan
  const isVspOptions = ancillaryPlan.carrier === 'VSP' && ancillaryPlan.name === 'EasyOptions'

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-row gap-4'>
        {ancillaryPlan.carrier_image_url && (
          <CarrierLogo aria-hidden src={ancillaryPlan.carrier_image_url} size='large' />
        )}
        <div className='flex flex-col gap-1'>
          <div className='heading-lg text-main'>{ancillaryPlan.name}</div>
          <div className='body text-gray-500'>
            {ancillaryPlan.carrier}
          </div>
        </div>
      </div>
      {isVisionPlan && (
        <div className='body text-main'>
          {ancillaryPlan.description}
        </div>
      )}
      {isDentalPlan && (
        <>
          {!ancillaryPlan.waiting_period && (
            <div>
              <Badge type='success'>No waiting period</Badge>
            </div>
          )}
          <DentalFeatures features={ancillaryPlan.features} />
        </>
      )}
      {
        isVisionPlan && !isVspOptions && (
          <VisionFeatures features={ancillaryPlan.features} />
        )
      }
      {isVspOptions &&
        <div className='w-full border border-gray-200 rounded-2xl p-4 gap-4 flex flex-col'>
          {ancillaryPlan.features.map((feature) => {
            return (
              <div key={feature.header} className='flex flex-col gap-2'>
                <div className='flex flex-row gap-1 items-center'>
                  <ShieldPlusIcon className='w-4 h-4 text-green-400' />
                  <p className='body-sm text-gray-500'>{feature.header}</p>
                </div>
                <p className='body-sm text-main'>{feature.descriptions[0]}</p>
              </div>
            )
          })}
        </div>}
    </div>
  )
}
